import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ApiClient } from '../../api'
import { useEvents } from '../../hooks/useEvents'
import { C, F, TTX } from './theme'
import { Icon } from './prims'
import { useForecastConflicts } from './useForecastConflicts'

// The NYMR Action Calendar context, pulled into the Forecast Studio: a month grid
// tinted by The Big Plan's running timetable (proxied via /api/forecast/timetable)
// with all other NYMR events shown as chips, so a proposed slot can be placed
// against everything else that's on. Click a day to set the slot (2-click range).

const WD = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const pad = (n: number) => String(n).padStart(2, '0')
const ymd = (y: number, m: number, d: number) => `${y}-${pad(m + 1)}-${pad(d)}`
const daysInMonth = (y: number, m: number) => new Date(Date.UTC(y, m + 1, 0)).getUTCDate()
const firstWeekdayMon = (y: number, m: number) => (new Date(Date.UTC(y, m, 1)).getUTCDay() + 6) % 7

function rangeInclusive(a: string, b: string): string[] {
  const s = new Date(`${a}T00:00:00Z`), e = new Date(`${(b || a)}T00:00:00Z`)
  if (isNaN(s.getTime()) || isNaN(e.getTime()) || e < s) return a ? [a] : []
  const out: string[] = []
  for (let d = new Date(s), i = 0; d <= e && i < 400; d.setUTCDate(d.getUTCDate() + 1), i++) out.push(d.toISOString().slice(0, 10))
  return out
}

export function CalendarContext({ eventId, proposedDates, onPickDay, calendar, onCalendarChange }: { eventId: number; proposedDates: string[]; onPickDay?: (date: string) => void; calendar?: string; onCalendarChange?: (id: string) => void }) {
  const { data: events } = useEvents()
  const init = proposedDates[0] ? new Date(`${proposedDates[0]}T00:00:00Z`) : new Date()
  const [y, setY] = useState(init.getUTCFullYear())
  const [m, setM] = useState(init.getUTCMonth())

  const from = ymd(y, m, 1)
  const to = ymd(y, m, daysInMonth(y, m))
  const ttQ = useQuery({ queryKey: ['fc-tt', from, to, calendar ?? ''], queryFn: () => ApiClient.forecastTimetable(from, to, calendar || undefined) })
  const calsQ = useQuery({ queryKey: ['fc-tt-cals'], queryFn: ApiClient.forecastTimetableCalendars, staleTime: 5 * 60 * 1000 })
  const calendars = calsQ.data?.timetables ?? []
  const days = ttQ.data?.days ?? {}
  const proposedSet = useMemo(() => new Set(proposedDates), [proposedDates])
  const { conflictDates } = useForecastConflicts(eventId, proposedDates)

  const byDate = useMemo(() => {
    const map = new Map<string, Array<{ name: string; bg: string; fg: string; border: string }>>()
    for (const ev of events ?? []) {
      if (ev.id === eventId || ev.status === 'Idea') continue
      const set = (ev.dates && ev.dates.length) ? ev.dates : rangeInclusive(ev.startDate, ev.endDate)
      for (const d of set) {
        if (d < from || d > to) continue
        const arr = map.get(d) ?? []
        arr.push({ name: ev.name, bg: ev.type.colourBg, fg: ev.type.colourText, border: ev.type.colourBorder })
        map.set(d, arr)
      }
    }
    return map
  }, [events, eventId, from, to])

  const lead = firstWeekdayMon(y, m)
  const total = daysInMonth(y, m)
  const cells: Array<number | null> = [...Array(lead).fill(null), ...Array.from({ length: total }, (_, i) => i + 1)]
  const prev = () => { if (m === 0) { setY(y - 1); setM(11) } else setM(m - 1) }
  const next = () => { if (m === 11) { setY(y + 1); setM(0) } else setM(m + 1) }

  const ttUnavailable = ttQ.isFetched && Object.keys(days).length === 0

  return (
    <div style={{ marginTop: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <NavBtn icon="chevL" onClick={prev} />
        <span style={{ fontFamily: F.serif, fontSize: 16, fontWeight: 800, color: C.ink, minWidth: 150, textAlign: 'center' }}>{MONTHS[m]} {y}</span>
        <NavBtn icon="chevR" onClick={next} />
        {calendars.length > 0 && (
          <select
            value={calendar ?? ''}
            onChange={(e) => onCalendarChange?.(e.target.value)}
            disabled={!onCalendarChange}
            title="Which timetable calendar to model against"
            style={{ fontFamily: F.body, fontSize: 12, fontWeight: 600, color: C.ink, background: C.card, border: `1px solid ${C.line}`, borderRadius: 8, padding: '6px 8px', maxWidth: 220 }}
          >
            <option value="">Current planned season</option>
            {calendars.map((c) => <option key={c.id} value={c.id}>{c.name}{c.status !== 'published' ? ` (${c.status})` : ''}</option>)}
          </select>
        )}
        <span style={{ marginLeft: 'auto', fontFamily: F.body, fontSize: 11, color: C.inkFaint }}>{onPickDay ? 'Click days to set the slot · other NYMR events shown' : 'Other NYMR events shown'}</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 4 }}>
        {WD.map((w) => <div key={w} style={{ fontFamily: F.body, fontSize: 10, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: C.inkFaint, textAlign: 'center', padding: '2px 0' }}>{w}</div>)}
        {cells.map((d, i) => {
          if (d == null) return <div key={`b${i}`} />
          const date = ymd(y, m, d)
          const tt = days[date]
          const ttx = tt && tt.run && tt.key ? (TTX[tt.key] ?? null) : null
          const evs = byDate.get(date) ?? []
          const proposed = proposedSet.has(date)
          const conflict = proposed && conflictDates.has(date)
          return (
            <button
              key={date}
              type="button"
              onClick={onPickDay ? () => onPickDay(date) : undefined}
              title={conflict ? 'Clashes with another NYMR event' : (ttx ? ttx.label : (tt && !tt.run ? 'No service' : ''))}
              style={{
                textAlign: 'left', font: 'inherit', cursor: onPickDay ? 'pointer' : 'default',
                minHeight: 62, padding: '5px 6px', borderRadius: 8,
                border: `1.5px solid ${conflict ? C.amber : proposed ? C.green : C.lineSoft}`,
                boxShadow: conflict ? `0 0 0 2px ${C.amber}55` : 'none',
                background: proposed ? C.greenBg : C.card, position: 'relative', overflow: 'hidden'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ fontFamily: F.mono, fontSize: 11, fontWeight: 700, color: proposed ? C.green : C.ink }}>{d}</span>
                {ttx && <span title={ttx.label} style={{ width: 7, height: 7, borderRadius: 4, background: ttx.dot, marginLeft: 'auto' }} />}
                {proposed && !conflict && <Icon name="check" size={11} color={C.green} />}
                {conflict && <span title="Clash with another event" style={{ marginLeft: ttx ? 0 : 'auto', display: 'flex' }}><Icon name="alert" size={11} color={C.amber} /></span>}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 3 }}>
                {evs.slice(0, 2).map((e, j) => (
                  <span key={j} title={e.name} style={{ display: 'block', fontFamily: F.body, fontSize: 9.5, fontWeight: 700, color: e.fg, background: e.bg, border: `1px solid ${e.border}`, borderRadius: 4, padding: '1px 4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.name}</span>
                ))}
                {evs.length > 2 && <span style={{ fontFamily: F.body, fontSize: 9, fontWeight: 700, color: C.inkFaint }}>+{evs.length - 2} more</span>}
              </div>
            </button>
          )
        })}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 10, alignItems: 'center' }}>
        <Legend swatch={C.greenBg} border={C.green} label="Proposed slot" />
        <Legend dot={TTX.red?.dot} label="Running-timetable colour" />
        <Legend swatch={C.card} border={C.lineSoft} label="Other NYMR events (chips)" />
        {conflictDates.size > 0 && <Legend swatch={C.greenBg} border={C.amber} label="Clash on proposed day" />}
        {ttUnavailable && <span style={{ fontFamily: F.body, fontSize: 11, color: C.brassDk }}>Timetable unavailable — showing events only.</span>}
        {ttQ.isLoading && <span style={{ fontFamily: F.body, fontSize: 11, color: C.inkFaint }}>Loading timetable…</span>}
      </div>
    </div>
  )
}

function NavBtn({ icon, onClick }: { icon: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: 8, border: `1px solid ${C.line}`, background: C.card, cursor: 'pointer', color: C.inkSoft }}>
      <Icon name={icon} size={16} />
    </button>
  )
}

function Legend({ swatch, border, dot, label }: { swatch?: string; border?: string; dot?: string; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontFamily: F.body, fontSize: 11, color: C.inkSoft }}>
      {dot ? <span style={{ width: 8, height: 8, borderRadius: 4, background: dot }} /> : <span style={{ width: 12, height: 12, borderRadius: 3, background: swatch, border: `1.5px solid ${border}` }} />}
      {label}
    </span>
  )
}
